(function () {
  'use strict';

  if (window.customElements && window.customElements.get('delivery-serviceability')) {
    return;
  }

  const STORAGE_KEY = 'theme:delivery-pincode';
  const DEFAULT_API = 'https://prod.api.sugarcosmetics.com/pincode/getServiceability';
  const PINCODE_RE = /^\d{6}$/;

  /*
   * In-memory, per-product state for the current page view only. This lets a
   * shown result survive Prestige's product-rerender (variant/qty change)
   * without persisting across navigations: every fresh PDP load starts empty,
   * so the shopper is asked for their pincode again on another product page.
   */
  window.deliveryServiceabilityState = window.deliveryServiceabilityState || {};

  const safeGet = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const safeSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* Safari private mode / storage-disabled contexts fail silently */
    }
  };

  const digitsOnly = (value) => (value || '').replace(/\D/g, '').slice(0, 6);

  const DeliveryServiceability = class extends HTMLElement {
    connectedCallback() {
      if (this._initialised) {
        return;
      }
      this._initialised = true;

      this.apiUrl = this.dataset.apiUrl || DEFAULT_API;
      this.remember = this.dataset.remember !== 'false';
      this.productKey = this.dataset.productId || 'default';

      this.form = this.querySelector('[data-do-form]');
      this.input = this.querySelector('[data-do-input]');
      this.submitButton = this.querySelector('[data-do-submit]');
      this.spinner = this.querySelector('.delivery-options__button-spinner');
      this.buttonLabel = this.querySelector('.delivery-options__button-label');
      this.errorEl = this.querySelector('[data-do-error]');
      this.inputView = this.querySelector('[data-do-view="input"]');
      this.resultView = this.querySelector('[data-do-view="result"]');
      this.pincodeEl = this.querySelector('[data-do-pincode]');
      this.changeButton = this.querySelector('[data-do-change]');
      this.deliveryEl = this.querySelector('[data-do-delivery]');
      this.deliveryRow = this.querySelector('[data-do-row="delivery"]');
      this.codEl = this.querySelector('[data-do-cod]');
      this.codRow = this.querySelector('[data-do-row="cod"]');
      this.shippingEl = this.querySelector('[data-do-shipping]');
      this.shippingRow = this.querySelector('[data-do-row="shipping"]');

      this.bindEvents();
      this.restore();
    }

    get pageState() {
      return window.deliveryServiceabilityState[this.productKey] || null;
    }

    set pageState(value) {
      if (value) {
        window.deliveryServiceabilityState[this.productKey] = value;
      } else {
        delete window.deliveryServiceabilityState[this.productKey];
      }
    }

    bindEvents() {
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.check(this.input ? this.input.value : '', false);
        });
      }

      if (this.input) {
        this.input.addEventListener('input', () => {
          this.input.value = digitsOnly(this.input.value);
          this.clearError();
        });
      }

      if (this.changeButton) {
        this.changeButton.addEventListener('click', () => {
          this.pageState = { editing: true, pincode: this.input ? this.input.value : '' };
          this.showInput(true);
        });
      }
    }

    restore() {
      const state = this.pageState;

      // A result was already shown on this page view: re-render it so the widget
      // survives a product-rerender (variant/quantity change) without re-fetching.
      if (state && state.data) {
        if (this.input) {
          this.input.value = state.pincode || '';
        }
        this.renderResult(state.pincode, state.data);
        return;
      }

      // The shopper was editing before a re-render: keep the input open.
      if (state && state.editing) {
        this.prefill(state.pincode);
        this.showInput(false);
        return;
      }

      // Fresh page view: pre-fill the last-used pincode for convenience but
      // require the shopper to tap Check (they are asked again on every PDP).
      if (this.remember) {
        const saved = safeGet(STORAGE_KEY);
        if (saved && PINCODE_RE.test(saved)) {
          this.prefill(saved);
        }
      }

      this.showInput(false);
    }

    prefill(pincode) {
      if (this.input && pincode) {
        this.input.value = digitsOnly(pincode);
      }
    }

    showInput(focus) {
      if (this.resultView) {
        this.resultView.hidden = true;
      }
      if (this.inputView) {
        this.inputView.hidden = false;
      }
      if (focus && this.input) {
        this.input.focus();
        this.input.select();
      }
    }

    showResult() {
      if (this.inputView) {
        this.inputView.hidden = true;
      }
      if (this.resultView) {
        this.resultView.hidden = false;
      }
    }

    setLoading(isLoading) {
      if (this.submitButton) {
        this.submitButton.disabled = isLoading;
      }
      if (this.spinner) {
        this.spinner.hidden = !isLoading;
      }
      if (this.buttonLabel) {
        this.buttonLabel.style.visibility = isLoading ? 'hidden' : '';
      }
    }

    showError(message) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
      if (this.form) {
        this.form.classList.add('is-invalid');
      }
    }

    clearError() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
      if (this.form) {
        this.form.classList.remove('is-invalid');
      }
    }

    buildUrl(pincode) {
      const separator = this.apiUrl.indexOf('?') === -1 ? '?' : '&';
      return `${this.apiUrl}${separator}pincode=${encodeURIComponent(pincode)}`;
    }

    check(rawValue, silent) {
      const pincode = digitsOnly(rawValue);

      if (!PINCODE_RE.test(pincode)) {
        this.showInput(true);
        if (!silent) {
          this.showError('Please enter a valid 6-digit pincode.');
        }
        return;
      }

      this.clearError();
      this.setLoading(true);

      fetch(this.buildUrl(pincode), { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((data) => {
          this.setLoading(false);

          if (!data || data.error) {
            this.showInput(true);
            if (!silent) {
              this.showError((data && data.message) || 'Please enter a valid 6-digit pincode.');
            }
            return;
          }

          this.pageState = { pincode, data };
          if (this.remember) {
            safeSet(STORAGE_KEY, pincode);
          }
          this.renderResult(pincode, data);
        })
        .catch(() => {
          this.setLoading(false);
          this.showInput(true);
          if (!silent) {
            this.showError('Could not check delivery right now. Please try again.');
          }
        });
    }

    friendlyEta(data) {
      try {
        if (!data || !data.serviceable || !data.edd || !data.edd.min_edd) {
          return null;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const minEdd = new Date(`${data.edd.min_edd}T00:00:00`);
        if (Number.isNaN(minEdd.getTime())) {
          return null;
        }
        const dayDiff = Math.round((minEdd.getTime() - today.getTime()) / 86400000);
        if (dayDiff <= 0) {
          return 'Get it by Today';
        }
        if (dayDiff === 1) {
          return 'Get it by Tomorrow';
        }
        return null;
      } catch (error) {
        return null;
      }
    }

    renderResult(pincode, data) {
      const messages = data.messages || {};
      const isServiceable = !!data.serviceable;

      if (this.pincodeEl) {
        this.pincodeEl.textContent = pincode;
      }

      const deliveryText =
        this.friendlyEta(data) ||
        messages.delivery ||
        (isServiceable ? 'Delivery available' : 'Delivery not available');

      if (this.deliveryEl) {
        this.deliveryEl.textContent = deliveryText;
      }
      if (this.deliveryRow) {
        this.deliveryRow.classList.toggle('is-negative', !isServiceable);
      }

      const codAvailable = !!data.cod_available;
      if (this.codEl) {
        this.codEl.textContent = codAvailable
          ? 'Pay on Delivery available'
          : 'Pay on Delivery is not available';
      }
      if (this.codRow) {
        this.codRow.classList.toggle('is-negative', !codAvailable);
      }

      const shippingText =
        messages.shipping ||
        (data.city && data.state ? `Shipping to: ${data.city}, ${data.state}` : '');

      if (this.shippingEl) {
        this.shippingEl.textContent = shippingText;
      }
      if (this.shippingRow) {
        this.shippingRow.hidden = shippingText === '';
      }

      this.showResult();
    }
  };

  window.customElements.define('delivery-serviceability', DeliveryServiceability);
})();
